'use client'

import { CheckBorderStyled, CheckSolidStyled, CheckboxStyled } from '@components/AddressAutocompleteForm.styles'
import InlineLoaderAnimation from '@lib/assets/lottie/inline-loader.json'

import { Autocomplete, Button, Chip, FormControlLabel, Stack, TextField, Typography } from '@hermes/web-components'
import type { Options } from '@hooks/useAutocompleteOptions'
import { useAutocompleteOptions } from '@hooks/useAutocompleteOptions'
import useTranslateMessage from '@hooks/useTranslateMessage'
import Lottie from 'lottie-react'
import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

type AutocompleteFieldProps = {
  name: string
  control: any
  label: string
  options: any[]
  onChange: (event: any, newValue: any) => void
  onInputChange?: (event: any, newInputValue: string) => void
  loading: boolean
  disabled?: boolean
  sx?: any
}

const SearchSelect: React.FC<AutocompleteFieldProps> = ({
  name,
  label,
  options,
  control,
  onChange,
  onInputChange,
  loading,
  disabled,
  sx
}) => {
  const menuInitialState = false
  const [menuIsOpen, setMenuIsOpen] = useState(menuInitialState)

  const { t } = useTranslateMessage()

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: t({ id: 'addressForm.formField.required', defaultMessage: 'Field is required' })
      }}
      defaultValue={null}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          id="field1"
          noOptionsText={t({ id: 'addressForm.noOptions', defaultMessage: 'No options' })}
          loadingText={t({ id: 'addressForm.loading', defaultMessage: 'Loading' })}
          disableClearable
          autoSelect
          autoHighlight
          fullWidth
          value={field.value}
          open={menuIsOpen}
          loading={loading}
          disabled={disabled ?? false}
          onFocus={() => setMenuIsOpen(true)}
          onOpen={() => setMenuIsOpen(true)}
          onClose={() => setMenuIsOpen(false)}
          onChange={(event, newValue) => {
            field.onChange(newValue)
            onChange(event, newValue)
          }}
          onInputChange={(event, newInputValue) => {
            if (onInputChange) {
              onInputChange(event, newInputValue)
            }
          }}
          options={options}
          getOptionLabel={(option) => option.label}
          getOptionKey={(option) => option.id}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              error={!!error}
              helperText={error ? error.message : ' '}
              sx={{
                marginBottom: error ? `${(error.message?.length ?? 0) * 1.5 || 24}px` : '0'
              }}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'off'
              }}
            />
          )}
          sx={() => ({
            ...sx,
            '.MuiOutlinedInput-notchedOutline': {
              border: 'none'
            },
            '.MuiAutocomplete-clearIndicator': {
              marginRight: '4px',
              color: 'white',
              backgroundColor: 'lightgrey',
              '&:hover': {
                color: 'white',
                backgroundColor: 'red'
              }
            }
          })}
        />
      )}
    />
  )
}

type AddressAutocompleteFormProps = {
  onSubmit: (data: any) => Promise<void>
  onClose: () => void
}

export const AddressAutocompleteForm: React.FC<AddressAutocompleteFormProps> = ({ onSubmit, onClose }) => {
  const { t } = useTranslateMessage()
  const { control, register, handleSubmit, resetField, getValues, setValue } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    criteriaMode: 'all'
  })

  const { options, optionsLoading, getOptions, invalidateOptions } = useAutocompleteOptions()

  const [formSubmitting, setFormSubmitting] = useState(false)

  let requiresFloorAndApartmentData = false
  if (getValues('streetNumber')?.value?.showHouseholds) {
    requiresFloorAndApartmentData = true
  }
  const addressFieldMapper: any = {
    streets: 'street',
    streetNumbers: 'streetNumber',
    floors: 'floor',
    households: 'household',
    zip: 'zip',
    city: 'city'
  }

  const nextFields: any = {
    streets: ['streetNumbers', 'floors', 'households', 'zip', 'city'],
    streetNumbers: ['floors', 'households', 'zip'],
    floors: ['households'],
    households: []
  }

  const resetPartial = (currentField: keyof Options) => {
    const fieldsToReset = nextFields[currentField]
    fieldsToReset.forEach((field: keyof Options) => {
      const addressField = addressFieldMapper[field]
      resetField(addressField)
    })
  }

  const handleChange = (selectedOption: any, type: keyof Options) => {
    if (selectedOption) {
      const nextField = nextFields[type][0]
      const context = {
        address: {
          street: getValues('street')?.value,
          streetNumber: getValues('streetNumber')?.value,
          floor: getValues('floor')?.value,
          household: getValues('household')?.value
        }
      }
      resetPartial(type)
      invalidateOptions(nextField)
      getOptions(nextField, '*', context)
      if (type === 'streets') setValue('city', selectedOption.value.city)
      if (type === 'streetNumbers') setValue('zip', selectedOption.value.postalCode)
    }
  }

  const handleInputChange = (inputValue: string, type: keyof Options) => {
    if (inputValue) {
      const context = {
        address: {
          street: getValues('street')?.value,
          streetNumber: getValues('streetNumber')?.value,
          floor: getValues('floor')?.value,
          household: getValues('household')?.value
        }
      }
      getOptions(type, inputValue, context)
    }
  }

  const onSubmitForm = async (data: any, e: any) => {
    setFormSubmitting(true)
    e.preventDefault()
    try {
      await onSubmit(data)
    } finally {
      setFormSubmitting(false)
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmitForm)} noValidate>
        <Typography variant="subtitle2" marginBottom={2}>
          {t({ id: 'addressForm.streetName.helperText.part1', defaultMessage: 'Tip: enter street name' })}{' '}
          <Chip
            label={','}
            size="small"
            disabled
            clickable={false}
            sx={{
              cursor: 'default',
              '&.Mui-disabled': {
                backgroundColor: (theme) => `${theme.palette.success.main} !important`,
                border: 'none !important'
              }
            }}
          />
          {t({ id: 'addressForm.streetName.helperText.part2', defaultMessage: 'and' })}{' '}
          <Chip
            label={t({ id: 'addressForm.streetName.helperText.label2', defaultMessage: 'zip code' })}
            size="small"
            disabled
            clickable={false}
            sx={{
              cursor: 'default',
              '&.Mui-disabled': {
                backgroundColor: (theme) => `${theme.palette.success.main} !important`,
                border: 'none !important'
              }
            }}
          />
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <SearchSelect
              name="street"
              control={control}
              label={t({ id: 'addressForm.streetName', defaultMessage: 'Street' })}
              options={options.streets}
              onChange={(e, v) => handleChange(v, 'streets')}
              onInputChange={(e) => handleInputChange(e?.target?.value, 'streets')}
              loading={optionsLoading}
              disabled={false}
              sx={{ width: '70%' }}
            />
            <SearchSelect
              name="streetNumber"
              control={control}
              label={t({ id: 'addressForm.streetNumber', defaultMessage: 'Street no.' })}
              options={options.streetNumbers}
              onChange={(e, v) => handleChange(v, 'streetNumbers')}
              onInputChange={(e) => handleInputChange(e?.target?.value, 'streetNumbers')}
              loading={optionsLoading}
              disabled={false}
              sx={{ width: '30%' }}
            />
          </Stack>
          {requiresFloorAndApartmentData && (
            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <SearchSelect
                name="floor"
                control={control}
                label={t({ id: 'addressForm.floor', defaultMessage: 'Floor' })}
                options={options.floors}
                onChange={(e, v) => handleChange(v, 'floors')}
                onInputChange={(e) => handleInputChange(e?.target?.value, 'floors')}
                loading={optionsLoading}
              />
              <SearchSelect
                name="household"
                control={control}
                label={t({ id: 'addressForm.household', defaultMessage: 'Household' })}
                options={options.households}
                onChange={(e, v) => handleChange(v, 'households')}
                onInputChange={(e) => handleInputChange(e?.target?.value, 'households')}
                loading={optionsLoading}
              />
            </Stack>
          )}
          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <TextField
              {...register('city')}
              label={t({ id: 'addressForm.city', defaultMessage: 'City' })}
              disabled={true}
              InputLabelProps={{
                shrink: !!getValues('city')
              }}
            />
            <TextField
              {...register('zip')}
              label={t({ id: 'addressForm.zip', defaultMessage: 'Zip' })}
              disabled={true}
              InputLabelProps={{
                shrink: !!getValues('zip')
              }}
            />
          </Stack>
          <FormControlLabel
            control={
              <CheckboxStyled
                {...register('isPrimary')}
                defaultChecked
                icon={<CheckBorderStyled className="icon" />}
                checkedIcon={<CheckSolidStyled className="checkedIcon" />}
              />
            }
            label={t({ id: 'addressForm.checkbox.setAsPrimary', defaultMessage: 'Set as primary address' })}
          />
          <Stack direction={'row'} spacing={2}>
            <Button variant="contained" type="submit" disabled={formSubmitting}>
              {formSubmitting ? (
                <Lottie animationData={InlineLoaderAnimation} loop style={{ width: 50, height: 50 }} />
              ) : (
                t({ id: 'button.save', defaultMessage: 'Save' })
              )}
            </Button>
            <Button variant="outlined" onClick={onClose}>
              {t({ id: 'button.back', defaultMessage: 'Back' })}
            </Button>
          </Stack>
        </Stack>
      </form>
    </>
  )
}
